import { fetchProductByHandle } from './kbite-notify-api.js';

const HR_RESULTS_SELECTOR = '.hr-results';
const HR_SEARCH_ACTIVE_CLASS = 'hr-search-disable-scroll';
const PRODUCT_HANDLE_RE = /\/products\/([^/?#]+)/;
const DEBUG_SEARCH_NOTIFY = false;

let scanScheduled = false;
const productJsonCache = new Map();

function debugSearchNotify(step, data = {}) {
  if (!DEBUG_SEARCH_NOTIFY) return;
  console.log(`[Search Notify] ${step}`, data);
}

function parseProductHandle(url) {
  if (!url) return '';

  try {
    const path = url.startsWith('http')
      ? new URL(url).pathname
      : url.split('?')[0].split('#')[0];

    return path.match(PRODUCT_HANDLE_RE)?.[1] || '';
  } catch {
    return url.match(PRODUCT_HANDLE_RE)?.[1] || '';
  }
}

function getNotifyLabel() {
  return window.FoxTheme?.variantStrings?.notifyMeWhenAvailable || 'Notify me when available';
}

function getButtonStyleClass() {
  return document.querySelector('product-card-notify')?.dataset.buttonStyle || 'btn--primary';
}

function getCartIconMarkup(form) {
  return (
    form.querySelector('.product-card__atc-icon')?.innerHTML?.trim() ||
    document.querySelector('.product-card .product-card__atc-icon')?.innerHTML?.trim() ||
    ''
  );
}

function getProductCard(form) {
  return (
    form.closest('.product-card') ||
    form.closest('.hr-search-overlay-product') ||
    form.closest('.swiper-slide') ||
    form.parentElement
  );
}

function getProductLink(form) {
  const card = getProductCard(form);

  return (
    card?.querySelector('a[href*="/products/"]') ||
    form.closest('.product-card__image-wrapper')?.querySelector('a[href*="/products/"]') ||
    null
  );
}

function parseShopifyId(value) {
  if (value == null || value === '') return '';

  const str = String(value).trim();
  const gidMatch = str.match(/\/(\d+)$/);

  return gidMatch?.[1] || str;
}

function readProductIdFromCard(card) {
  if (!card) return '';

  const source =
    card.querySelector('[data-product-id]') ||
    (card.matches?.('[data-product-id]') ? card : null);

  return parseShopifyId(source?.dataset?.productId || source?.getAttribute('data-product-id'));
}

function readVariantIdFromForm(form) {
  const sources = [
    form.querySelector('input[name="id"]'),
    form.querySelector('input[name="variant-id"]'),
    form.querySelector('.product-variant-id'),
    form.querySelector('[data-variant-id]'),
  ];

  for (const source of sources) {
    const value =
      source?.value ||
      source?.dataset?.variantId ||
      source?.getAttribute('data-variant-id');

    if (value) return parseShopifyId(value);
  }

  return '';
}

function readProductIdFromForm(form) {
  const fromInput = form.querySelector('input[name="product-id"]')?.value;
  if (fromInput) return parseShopifyId(fromInput);

  const fromForm = form.dataset.productId || form.getAttribute('data-product-id');
  if (fromForm) return parseShopifyId(fromForm);

  return readProductIdFromCard(getProductCard(form));
}

async function fetchCachedProductByHandle(handle) {
  if (!handle) return null;

  if (productJsonCache.has(handle)) {
    return productJsonCache.get(handle);
  }

  const product = await fetchProductByHandle(handle);
  productJsonCache.set(handle, product);

  return product;
}

async function resolveFormProductContext(form) {
  const link = getProductLink(form);
  const href = link?.getAttribute('href') || link?.href || '';
  const handle = parseProductHandle(href);

  let productId = readProductIdFromForm(form);
  let variantId = readVariantIdFromForm(form);

  if (!handle) {
    debugSearchNotify('skip:missing-handle', { form });
    return null;
  }

  let product = null;

  if (!productId || !variantId) {
    product = await fetchCachedProductByHandle(handle);

    if (!product) {
      debugSearchNotify('skip:product-fetch-failed', { handle, form });
      return null;
    }

    if (!productId) {
      productId = parseShopifyId(product.id);
    }

    if (!variantId) {
      const soldOutVariant = product.variants?.find((variant) => variant.available === false);
      const fallbackVariant = soldOutVariant || product.variants?.[0];
      variantId = parseShopifyId(fallbackVariant?.id);
    }
  }

  if (!productId || !variantId) {
    debugSearchNotify('skip:missing-ids-after-resolve', {
      handle,
      productId,
      variantId,
      form,
    });

    return null;
  }

  if (!product) {
    product = await fetchCachedProductByHandle(handle);
  }

  if (product?.variants?.length) {
    const hasVariant = product.variants.some(
      (variant) => parseShopifyId(variant.id) === variantId
    );

    if (!hasVariant) {
      const soldOutVariant = product.variants.find((variant) => variant.available === false);
      const fallbackVariant = soldOutVariant || product.variants[0];
      variantId = parseShopifyId(fallbackVariant?.id);
    }
  }

  return { handle, productId, variantId };
}

async function createNotifyButtonFromForm(form) {
  const context = await resolveFormProductContext(form);
  if (!context) return null;

  const { handle, productId, variantId } = context;
  const button = document.createElement('button');

  button.type = 'button';
  button.className = `product-card__atc product-card__action-button btn ${getButtonStyleClass()} product-form__notify-trigger product-card__notify`;

  button.dataset.action = 'open-product-card-notify';
  button.dataset.productId = String(productId);
  button.dataset.productHandle = handle;
  button.dataset.variantId = String(variantId);
  button.dataset.productUrl = `/products/${handle}`;

  button.setAttribute('data-action', 'open-product-card-notify');
  button.setAttribute('data-product-id', String(productId));
  button.setAttribute('data-product-handle', handle);
  button.setAttribute('data-variant-id', String(variantId));
  button.setAttribute('data-product-url', `/products/${handle}`);

  button.setAttribute('aria-label', getNotifyLabel());

  button.innerHTML = `
    <div class="btn__text flex gap-1 items-center">
      <span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">${getCartIconMarkup(form)}</span>
      <span class="product-card__atc-text product-card__action-text">${getNotifyLabel()}</span>
    </div>`;

  return button;
}

function isSoldOutForm(form) {
  if (form.dataset.searchNotifyReplaced === 'true') return false;
  if (form.dataset.searchNotifyPending === 'true') return false;
  if (form.querySelector('[data-action="open-product-card-notify"]')) return false;

  const button = form.querySelector('button');
  if (!button) return false;

  return button.disabled || button.getAttribute('aria-disabled') === 'true';
}

async function replaceSoldOutForm(form) {
  if (form.dataset.searchNotifyReplaced === 'true') return;
  if (form.dataset.searchNotifyPending === 'true') return;

  form.dataset.searchNotifyPending = 'true';

  const oldButton = form.querySelector('button');
  const notifyButton = await createNotifyButtonFromForm(form);

  if (!oldButton || !notifyButton) {
    delete form.dataset.searchNotifyPending;
    return;
  }

  oldButton.replaceWith(notifyButton);

  form.dataset.searchNotifyReplaced = 'true';
  form.setAttribute('data-search-notify-replaced', 'true');
  delete form.dataset.searchNotifyPending;

  debugSearchNotify('replaced-button', {
    form,
    productId: notifyButton.dataset.productId,
    variantId: notifyButton.dataset.variantId,
    handle: notifyButton.dataset.productHandle,
  });
}

async function scanSearchCards() {
  if (!hasActiveHelloRetailSearch()) return;
  if (!document.querySelector('product-card-notify')) return;
  if (window.FoxTheme?.settings?.pcardEnableKbiteNotify === false) return;

  const forms = [
    ...document.querySelectorAll(
      `${HR_RESULTS_SELECTOR} form[data-type="add-to-cart-form"]`
    ),
  ].filter(isSoldOutForm);

  debugSearchNotify('forms-found', {
    count: forms.length,
    forms,
  });

  await Promise.all(forms.map(replaceSoldOutForm));
}

function hasActiveHelloRetailSearch() {
  return Boolean(
    document.documentElement.classList.contains(HR_SEARCH_ACTIVE_CLASS) ||
    document.body.classList.contains(HR_SEARCH_ACTIVE_CLASS) ||
    document.querySelector(`${HR_RESULTS_SELECTOR} form[data-type="add-to-cart-form"]`)
  );
}

function scheduleScan() {
  if (scanScheduled) return;

  scanScheduled = true;

  requestAnimationFrame(() => {
    scanScheduled = false;
    void scanSearchCards();
  });
}

function mutationsTouchHelloRetailProducts(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      if (
        node.matches?.(
          `${HR_RESULTS_SELECTOR}, [class*="hr-"], [id*="helloretail"], form[data-type="add-to-cart-form"]`
        ) ||
        node.querySelector?.(
          `${HR_RESULTS_SELECTOR}, [class*="hr-"], [id*="helloretail"], form[data-type="add-to-cart-form"]`
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

function initSearchProductCardNotify() {
  if (window.FoxTheme?.settings?.pcardEnableKbiteNotify === false) return;

  const observer = new MutationObserver((mutations) => {
    if (mutationsTouchHelloRetailProducts(mutations)) {
      scheduleScan();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchProductCardNotify);
} else {
  initSearchProductCardNotify();
}